<template>
  <section class="multi-offer-container">
    <div id="multi-offer-view" class="container multi-offer-content">
      <div v-if="isLoadingOffer" class="multi-offer-skeleton" aria-hidden="true">
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton skeleton-filter"></div>
        <div class="skeleton skeleton-tabs"></div>
      </div>

      <template v-else-if="offer.tcm">
        <div data-testid="multi-offers-revamp" class="destinations-header">
          <h2 class="title">{{ translate('offCheckOutOurDestinations') }}</h2>

          <MultiOffersLocationFilter
            :locations="allLocations"
            @selectedLocation="locationSelected"
          />
        </div>

        <div v-if="isLoadingDestinationsContent" class="tabs-skeleton" aria-hidden="true">
          <div class="skeleton skeleton-tabs"></div>
        </div>

        <tabs v-else-if="!emptyResults" />
      </template>
    </div>

    <div v-if="displayMap" class="pricemap-container">
      <div class="container">
        <h2 class="title">{{ translate('offExploreHotels') }}</h2>
      </div>

      <div class="pricemap-content">
        <div v-if="isLoadingOffer" class="container pricemap-skeleton" aria-hidden="true">
          <div class="skeleton skeleton-map"></div>
        </div>

        <PriceMap
          :hotels="allHotels || []"
          :prices="allPrices || []"
          :clusterOptions="{
            minimumClusterSize: 2,
            gridSize: 30,
            markerPadding: 10
          }"
          :zoom="zoom"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import Tabs from '@/components/multi-offers/Tabs'
import MultiOffersLocationFilter from '@/components/multi-offers/Filters/MultiOffersLocationFilter'

const { mapActions, mapGetters } = createNamespacedHelpers('offersDestinations')

export default {
  name: 'MultiOffers',

  components: {
    MultiOffersLocationFilter,
    Tabs,
    PriceMap: () => import('@/components/multi-offers/PriceMap')
  },

  data: () => ({
    zoom: Number(global.appGlobalProps.offersData.zoom) || 2,
    displayMap: global.appGlobalProps.offersData.displayMap,
    isLoadingOffer: true,
    isLocationRequestFinished: false
  }),

  async created() {
    try {
      await this.getLocation()
      this.getPrices()
    } finally {
      this.isLocationRequestFinished = true
      this.isLoadingOffer = false
      this.$store.dispatch('sendAnalyticsView')
    }
  },

  computed: {
    ...mapGetters([
      'allLocations',
      'allHotels',
      'allPrices',
      'offer',
      'allDestinations',
      'hotDestinations'
    ]),

    hasDestinationsContent() {
      return (this.allDestinations || []).length > 0 || (this.hotDestinations || []).length > 0
    },

    emptyResults() {
      return (
        this.isLocationRequestFinished &&
        (this.allDestinations || []).length === 0 &&
        (this.hotDestinations || []).length === 0
      )
    },

    isLoadingDestinationsContent() {
      return this.offer && this.offer.tcm && !this.hasDestinationsContent && !this.emptyResults
    }
  },

  methods: {
    ...mapActions(['getLocation', 'getPrices', 'selectLocation']),

    async locationSelected(location) {
      await this.selectLocation(location)
    }
  }
}
</script>

<style scoped lang="scss">
$price-map-height: 71.8rem;

.multi-offer-container {
  margin-bottom: 2.5rem;
}

.multi-offer-content {
  min-height: 16rem;
}

.destinations-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-top: 2.5rem;
}

.title {
  font-size: 2.25rem;
  font-weight: 400;
  margin-bottom: 2.5rem;
}

.pricemap-container {
  width: 100vw;
  margin-top: 7.4rem;
  min-height: $price-map-height;
}

.pricemap-content {
  position: relative;
  min-height: $price-map-height;
}

.multi-offer-skeleton {
  padding-top: 2.5rem;
}

.tabs-skeleton {
  width: 100%;
  min-height: 8rem;
}

.pricemap-skeleton {
  position: absolute;
  inset: 0;
  z-index: 1;
  min-height: $price-map-height;
  pointer-events: none;
}

.skeleton {
  display: block;
  background: linear-gradient(
    90deg,
    var(--color-grey-light) 25%,
    var(--color-grey) 37%,
    var(--color-grey-light) 63%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
  border-radius: 0.25rem;
}

.skeleton-title {
  width: 18rem;
  max-width: 70%;
  height: 2.75rem;
  margin-bottom: 2.5rem;
}

.skeleton-filter {
  width: 14rem;
  height: 2.5rem;
  margin-bottom: 2rem;
}

.skeleton-tabs {
  width: 100%;
  height: 8rem;
}

.skeleton-map {
  width: 100%;
  height: $price-map-height;
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}
</style>
